import {
  getExperienceKeyForUtmContent,
  getPostLoginDestination,
  isValidPostLoginUtmContent,
  POST_LOGIN_CAMPAIGN_INTENT_SOURCE,
  POST_LOGIN_EXPERIENCE_KEY,
  POST_LOGIN_FALLBACK_REASON,
} from '../constants/postLoginExperience';
import { paths } from '../routes';
import {
  recordPostLoginExperienceFallbackEvent,
  recordPostLoginExperienceLandedEvent,
} from '../service/portalEventsClient';
import {
  clearPostLoginUtmContent,
  getPostLoginUtmContent,
} from './postLoginAttribution';
import { redirect } from './redirect';

const POST_LOGIN_SURFACE_OUTCOME_STORAGE_KEY =
  'soc_post_login_surface_outcome:v1';
const recordedPostLoginOutcomeIds = new Set();

export const hasRecordedPostLoginSurfaceOutcome = (outcomeId) => {
  const normalizedOutcomeId = String(outcomeId || '').trim();
  if (!normalizedOutcomeId) return false;
  if (recordedPostLoginOutcomeIds.has(normalizedOutcomeId)) return true;
  try {
    return (
      window.sessionStorage.getItem(POST_LOGIN_SURFACE_OUTCOME_STORAGE_KEY) ===
      normalizedOutcomeId
    );
  } catch (_error) {
    return false;
  }
};

const createPostLoginOutcomeId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const isPostLoginFallbackDecision = (decision) =>
  decision?.experienceKey === POST_LOGIN_EXPERIENCE_KEY.HOME ||
  Boolean(decision?.fallbackReason);

export const recordPostLoginExperiencePortalEvent = ({ decision, debtId } = {}) => {
  const normalizedDebtId = String(debtId || '').trim();
  if (!normalizedDebtId || !decision) return;

  if (isPostLoginFallbackDecision(decision)) {
    recordPostLoginExperienceFallbackEvent({
      debtIdentifier: normalizedDebtId,
      utmContent: decision.utmContent,
      fallbackReason: decision.fallbackReason,
    });
    return;
  }

  recordPostLoginExperienceLandedEvent({
    debtIdentifier: normalizedDebtId,
    utmContent: decision.utmContent,
    experienceKey: decision.experienceKey,
  });
};

export const recordPostLoginSurfaceOutcome = ({
  campaignHomeIntent,
  debtId,
  rendered,
  fallbackReason = POST_LOGIN_FALLBACK_REASON.OFFER_UNAVAILABLE,
  recordEvent = recordPostLoginExperiencePortalEvent,
} = {}) => {
  const normalizedDebtId = String(
    debtId || campaignHomeIntent?.debtId || ''
  ).trim();
  const outcomeId = String(campaignHomeIntent?.outcomeId || '').trim();
  const experienceKey = String(
    campaignHomeIntent?.experienceKey || ''
  ).trim();
  const utmContent = String(campaignHomeIntent?.utmContent || '').trim();

  if (
    campaignHomeIntent?.source !== POST_LOGIN_CAMPAIGN_INTENT_SOURCE ||
    !normalizedDebtId ||
    !outcomeId ||
    !experienceKey ||
    !utmContent
  ) {
    return false;
  }

  let alreadyRecorded = recordedPostLoginOutcomeIds.has(outcomeId);
  try {
    alreadyRecorded =
      alreadyRecorded ||
      window.sessionStorage.getItem(POST_LOGIN_SURFACE_OUTCOME_STORAGE_KEY) ===
        outcomeId;
  } catch (_error) {
    // Module memory still prevents duplicate events when storage is unavailable.
  }
  if (alreadyRecorded) return false;

  recordedPostLoginOutcomeIds.add(outcomeId);
  try {
    window.sessionStorage.setItem(
      POST_LOGIN_SURFACE_OUTCOME_STORAGE_KEY,
      outcomeId
    );
  } catch (_error) {
    // Recording the analytics attempt is more important than storage persistence.
  }

  recordEvent({
    debtId: normalizedDebtId,
    decision: rendered
      ? {
          experienceKey,
          utmContent,
          fallbackReason: null,
        }
      : {
          experienceKey: POST_LOGIN_EXPERIENCE_KEY.HOME,
          utmContent,
          fallbackReason,
        },
  });
  return true;
};

export const buildPostLoginRouteDecision = (experienceResponse = {}) => {
  const experienceKey =
    experienceResponse.experienceKey || POST_LOGIN_EXPERIENCE_KEY.HOME;
  const destination = getPostLoginDestination(experienceKey);

  return {
    route: destination.route || paths.home,
    campaignHomeCta: destination.campaignHomeCta,
    experienceKey,
    utmContent: experienceResponse.utmContent ?? null,
    fallbackReason: experienceResponse.fallbackReason ?? null,
  };
};

export const resolvePostLoginRoute = ({
  debtId,
  utmContent,
} = {}) => {
  const normalizedDebtId = String(debtId || '').trim();
  const normalizedUtmContent = String(utmContent || '').trim();

  if (!normalizedDebtId || !normalizedUtmContent) {
    return buildPostLoginRouteDecision({
      experienceKey: POST_LOGIN_EXPERIENCE_KEY.HOME,
      utmContent: normalizedUtmContent || null,
      fallbackReason: POST_LOGIN_FALLBACK_REASON.UNKNOWN_UTM,
    });
  }

  if (!isValidPostLoginUtmContent(normalizedUtmContent)) {
    return buildPostLoginRouteDecision({
      experienceKey: POST_LOGIN_EXPERIENCE_KEY.HOME,
      utmContent: normalizedUtmContent,
      fallbackReason: POST_LOGIN_FALLBACK_REASON.UNKNOWN_UTM,
    });
  }

  const experienceKey = getExperienceKeyForUtmContent(normalizedUtmContent);
  return buildPostLoginRouteDecision({
    experienceKey,
    utmContent: normalizedUtmContent,
    fallbackReason: null,
  });
};

export const applyPostLoginRouteDecision = ({
  decision,
  debtId,
  setCampaignHomeIntent,
  redirectFn = redirect,
  createOutcomeId = createPostLoginOutcomeId,
}) => {
  const nextCampaignHomeIntent =
    decision?.campaignHomeCta &&
    !isPostLoginFallbackDecision(decision)
      ? {
          cta: decision.campaignHomeCta,
          debtId: String(debtId),
          experienceKey: decision.experienceKey,
          outcomeId: createOutcomeId(),
          source: POST_LOGIN_CAMPAIGN_INTENT_SOURCE,
          utmContent: decision.utmContent,
        }
      : null;

  if (typeof setCampaignHomeIntent === 'function') {
    setCampaignHomeIntent(nextCampaignHomeIntent);
  }

  redirectFn(decision?.route || paths.home);
};

export const tryPostLoginExitRoute = async ({
  debtId,
  setCampaignHomeIntent,
  setPostLoginExperience,
  utmContent = getPostLoginUtmContent(),
  redirectFn = redirect,
} = {}) => {
  if (!utmContent) {
    return false;
  }

  const decision = resolvePostLoginRoute({
    debtId,
    utmContent,
  });

  const isFallback = isPostLoginFallbackDecision(decision);
  if (isFallback) {
    recordPostLoginExperiencePortalEvent({ decision, debtId });
  }

  clearPostLoginUtmContent();

  if (typeof setPostLoginExperience === 'function') {
    setPostLoginExperience({ utmContent: null });
  }

  if (isFallback) {
    if (typeof setCampaignHomeIntent === 'function') {
      setCampaignHomeIntent(null);
    }
    return false;
  }

  applyPostLoginRouteDecision({
    decision,
    debtId,
    setCampaignHomeIntent,
    redirectFn,
  });

  return true;
};
